import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { TextAreaFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { type IntakeCase, usePostLogCommunicationMutation } from 'store/api/intakeCaseApi';

interface LogCommunicationDialogProps {
    isOpen: boolean;
    onClose: () => void;
    intakeCase: Pick<IntakeCase, 'intakeCaseId' | 'outreaches'>;
}

const OutreachOrganization = {
    PRESCRIBER: 'Prescriber',
    PHARMACY: 'Pharmacy',
    HEALTH_PLAN: 'Health Plan',
} as const;

type OutreachOrganizationType = (typeof OutreachOrganization)[keyof typeof OutreachOrganization];

const ActionPlan = {
    FMH_TO_SUBMIT: 'FMH to submit',
    PRESCRIBER_TO_SUBMIT: 'Prescriber to submit',
} as const;

const ValidationSchema = Yup.object({
    outreachOrganization: Yup.string().oneOf(Object.values(OutreachOrganization)).required(),
    outreachDate: Yup.string().required(),
    communicationDirection: Yup.string().required(),
    completePrescriberOutreach: Yup.bool().when('outreachOrganization', {
        is: (outreachOrganization: OutreachOrganizationType) =>
            outreachOrganization === OutreachOrganization.PRESCRIBER,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        otherwise: (schema) => schema.notRequired(),
    }),
    actionPlan: Yup.string().when('completePrescriberOutreach', {
        is: (completePrescriberOutreach: boolean) => completePrescriberOutreach,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
    }),
    note: Yup.string(),
});

export const LogCommunicationDialog = ({
    isOpen,
    onClose,
    intakeCase,
}: LogCommunicationDialogProps) => {
    const now = dayjs();

    const prescriberOutreaches =
        intakeCase?.outreaches?.filter((o) => o.outreachOrganization === 'Prescriber') ?? [];

    const [postLogCommunication] = usePostLogCommunicationMutation();
    return (
        <Formik
            initialValues={
                { outreachDate: now.toISOString() } as Yup.InferType<typeof ValidationSchema>
            }
            validationSchema={ValidationSchema}
            enableReinitialize
            onSubmit={async (values, { resetForm }) => {
                await postLogCommunication({
                    intakeCaseId: intakeCase.intakeCaseId,
                    outreachOrganization: values.outreachOrganization,
                    outreachDate: values.outreachDate,
                    communicationDirection: values.communicationDirection,
                    completePrescriberOutreach: values.completePrescriberOutreach,
                    actionPlan: values.actionPlan,
                    note: values.note,
                }).unwrap();
                SuccessToast({ message: 'Communication Logged Successfully.' });
                resetForm();
                onClose();
            }}
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle="Log Communication"
                    primaryButtonLabel="Log Communication"
                    secondaryButtonLabel="Return To Intake Form"
                    isPrimaryButtonDisabled={!(formik.isValid && formik.dirty)}
                    onSecondaryButtonClick={(e) => {
                        onClose();
                        formik.handleReset(e);
                    }}
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="outreachOrganization"
                                label="Outreach Organization"
                                placeholderText="Select Outreach Organization"
                                required
                                values={Object.values(OutreachOrganization)}
                                onChange={(newValue) => {
                                    (async () => {
                                        const isPrescriber =
                                            newValue === OutreachOrganization.PRESCRIBER;
                                        await Promise.all([
                                            formik.setFieldValue('outreachOrganization', newValue),
                                            formik.setFieldValue(
                                                'completePrescriberOutreach',
                                                isPrescriber ? false : undefined, // Initialize when prescriber
                                            ),
                                            formik.setFieldValue('actionPlan', undefined), // Reset action plan when swapping orgs
                                        ]);
                                    })();
                                }}
                                disableAutoPopulate
                            />
                            <RadioButtonGroupFormik
                                name="communicationDirection"
                                label="Communication Direction"
                                required
                                options={['Inbound', 'Outbound']}
                            />
                            <DateInputFormik id="outreachDate" label="Date" required showTime />
                            {formik.values.outreachOrganization ===
                                OutreachOrganization.PRESCRIBER && (
                                <>
                                    <CheckboxListFormik
                                        name="completePrescriberOutreach"
                                        disabled={prescriberOutreaches.some((o) => o.isComplete)}
                                        options={['Complete Prescriber Outreach']}
                                        values={
                                            formik.values.completePrescriberOutreach
                                                ? ['Complete Prescriber Outreach']
                                                : []
                                        }
                                        onChange={(values) => {
                                            (async () =>
                                                formik.setFieldValue(
                                                    'completePrescriberOutreach',
                                                    values.length === 1,
                                                ))();
                                        }}
                                    />
                                    {formik.values.completePrescriberOutreach && (
                                        <SuggestSingleSelectFormik
                                            id="actionPlan"
                                            label="Action Plan"
                                            values={Object.values(ActionPlan)}
                                            required
                                        />
                                    )}
                                </>
                            )}
                            <TextAreaFormik id="note" label="Note" />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
